import { cn } from "@/lib/utils";
import { Check, Minus } from "lucide-react";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

/** Figma: size = 24px / 20px / 16px */
export type RoundCheckboxSize = 24 | 20 | 16;
/** Figma: state = unchecked / checked / partial */
export type RoundCheckboxState = "unchecked" | "checked" | "partial";

interface RoundCheckboxProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  size?: RoundCheckboxSize;
  state?: RoundCheckboxState;
  onChange?: (state: RoundCheckboxState) => void;
}

// ============================================
// Figma 디자인 토큰
// ============================================

/** Box 사이즈 (Figma number/24, number/20, number/16) */
const boxSizeStyles: Record<RoundCheckboxSize, string> = {
  24: "size-6",
  20: "size-5",
  16: "size-4",
};

/** Icon 사이즈 (Figma: 12px / 10px / 8px) */
const iconSizeStyles: Record<RoundCheckboxSize, string> = {
  24: "size-3",
  20: "size-2.5",
  16: "size-2",
};

/**
 * Box opacity (Figma)
 * - unchecked + enabled : 24%
 * - * + disabled        : 8%
 * - checked/partial + enabled : 100%
 */
const getBoxOpacity = (
  state: RoundCheckboxState,
  disabled: boolean,
): string => {
  if (disabled) return "opacity-8";
  if (state === "unchecked") return "opacity-24";
  return "";
};

// ============================================
// 컴포넌트
// ============================================

export const RoundCheckbox = forwardRef<HTMLButtonElement, RoundCheckboxProps>(
  (
    {
      size = 24,
      state = "unchecked",
      disabled = false,
      className,
      onChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      const next: RoundCheckboxState =
        state === "checked" ? "unchecked" : "checked";
      onChange?.(next);
      onClick?.(e);
    };

    const Icon = state === "partial" ? Minus : Check;

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={state === "partial" ? "mixed" : state === "checked"}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "group relative inline-flex items-center justify-center rounded-full",
          "focus-visible:outline-none",
          boxSizeStyles[size],
          !disabled && "cursor-pointer",
          disabled && "cursor-not-allowed",
          // Halo (Figma Decorate/Interaction/Neutral): box 외부 4px 확장
          "before:absolute before:-inset-1 before:rounded-full before:bg-neutral-950 before:transition-opacity before:pointer-events-none before:opacity-0",
          !disabled &&
            "hover:before:opacity-8 focus-visible:before:opacity-12 active:before:opacity-16",
          className,
        )}
        {...props}
      >
        {/* Box (state별 opacity) */}
        <span
          className={cn(
            "relative inline-flex items-center justify-center rounded-full bg-label-normal transition-opacity",
            boxSizeStyles[size],
            getBoxOpacity(state, disabled),
          )}
        >
          <Icon
            className={cn("text-static-white", iconSizeStyles[size])}
            strokeWidth={3}
            aria-hidden
          />
        </span>
      </button>
    );
  },
);

RoundCheckbox.displayName = "RoundCheckbox";

export default RoundCheckbox;
